import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "./ui/badge";
import { BadgeCheck, Sparkles, MapPin, Phone, ArrowUpRight, Clock } from "lucide-react";
import { CATEGORY_META } from "../lib/categories";

export function BusinessCard({ b, idx = 0 }) {
  const meta = CATEGORY_META[b.category_slug] || {};
  const Icon = meta.Icon;
  const isSponsored = b.sponsored_until && new Date(b.sponsored_until) > new Date();
  const isVerified = b.verified || b.plan !== "free";

  return (
    <Link
      to={`/business/${b.id}`}
      data-testid={`biz-card-${b.id}`}
      className="card-lift group block rounded-lg border border-zinc-200 bg-white p-5 reveal"
      style={{ animationDelay: `${Math.min(idx * 40, 280)}ms` }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          {b.logo ? (
            <div className="h-10 w-10 rounded-md border border-zinc-200 bg-white grid place-items-center overflow-hidden shrink-0">
              <img src={b.logo} alt={`${b.name} logo`} className="h-full w-full object-contain" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { className: "text-zinc-400 text-xs" })); }} />
            </div>
          ) : (
            <div
              className="h-10 w-10 rounded-md grid place-items-center text-white"
              style={{ background: meta.accent || "#0F172A" }}
            >
              {Icon && <Icon className="h-5 w-5" />}
            </div>
          )}
          <div>
            <div className="font-display text-lg leading-tight flex items-center gap-1.5">
              {b.name}
              {isVerified && <BadgeCheck className="h-4 w-4 text-blue-600" />}
            </div>
            <div className="text-xs text-zinc-500 font-mono-tag uppercase tracking-wider">{b.category_slug.replace(/-/g," ")}</div>
          </div>
        </div>
        <ArrowUpRight className="h-4 w-4 text-zinc-300 group-hover:text-zinc-900 transition" />
      </div>

      {b.description && <p className="text-sm text-zinc-600 line-clamp-2 mb-4">{b.description}</p>}

      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
        {b.address && (<span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{b.address}</span>)}
        {b.phone && (<span className="flex items-center gap-1"><Phone className="h-3 w-3" />{b.phone}</span>)}
      </div>

      <div className="flex items-center gap-2 mt-4">
        {isSponsored && (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100" data-testid={`biz-card-sponsored-${b.id}`}>
            <Sparkles className="h-3 w-3 mr-1" />Sponsored
          </Badge>
        )}
        {b.plan === "premium" && <Badge variant="outline" className="border-blue-200 text-blue-700">Premium</Badge>}
        {b.plan === "pro" && <Badge variant="outline" className="border-zinc-300">Pro</Badge>}
        {b.response_time_label && (
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50" data-testid={`biz-card-response-time-${b.id}`}>
            <Clock className="h-3 w-3 mr-1" />{b.response_time_label}
          </Badge>
        )}
        <span className="ml-auto text-xs text-zinc-400 font-mono-tag">{b.views || 0} views</span>
      </div>
    </Link>
  );
}
